import { MeshDTO } from '@model/meshDTO.ts';
import {
  BufferAttribute,
  BufferGeometry,
  Curve,
  DoubleSide,
  Float32BufferAttribute,
  Line3,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Uint32BufferAttribute,
  Vector3,
} from 'three';
import { Brush, CSGOperation, Evaluator } from 'three-bvh-csg';
import { ExtendedTriangle } from 'three-mesh-bvh';
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter.js';

// @ts-nocheck

function createGeometry(meshGeometry: MeshDTO, vertexColors?: number[]): BufferGeometry {
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(meshGeometry.positions, 3));
  geometry.setIndex(new Uint32BufferAttribute(meshGeometry.indices, 1));
  if (vertexColors) {
    const colors = new Float32Array(vertexColors);
    geometry.setAttribute('color', new BufferAttribute(colors, 3));
  }
  return geometry;
}

function createSubMesh(vertices: Set<number>, geometry: BufferGeometry, inverse = false): subMesh {
  const indexAttr = geometry.getIndex();
  const positionAttr = geometry.getAttribute('position');
  const colorAttr = geometry.getAttribute('color');
  const newIndices: number[] = [];
  const newPositions: number[] = [];
  const newColors: number[] = [];
  const indexMap: Map<number, number> = new Map(); // To track new index positions
  // Iterate over the original geometry indices
  for (let j = 0; j < indexAttr?.count; j += 3) {
    if (!indexAttr?.getX) {
      throw new Error('Index attribute not found');
    }
    const indexX = indexAttr.getX(j);
    const indexY = indexAttr.getY(j);
    const indexZ = indexAttr.getZ(j);
    // Check if all indices of this triangle are in the tooth set
    if (vertices.has(indexX) === !inverse && vertices.has(indexY) === !inverse && vertices.has(indexZ) === !inverse) {
      // Process each index (X, Y, Z) for the triangle
      [indexX, indexY, indexZ].forEach((index) => {
        if (!indexMap.has(index)) {
          // Add new vertex data if not already mapped
          indexMap.set(index, newPositions.length / 3);
          newPositions.push(positionAttr.getX(index), positionAttr.getY(index), positionAttr.getZ(index));
          if (colorAttr) {
            newColors.push(colorAttr.getX(index), colorAttr.getY(index), colorAttr.getZ(index));
          }
        }
        // Push new index based on the mapped index
        newIndices.push(indexMap.get(index)!);
      });
    }
  }
  //newGeometry.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3));
  return { indices: newIndices, positions: newPositions, colors: newColors };
}

function solidModelling(geometry1: BufferGeometry, geometry2: BufferGeometry, operation: CSGOperation): BufferGeometry {
  if (geometry1.getAttribute('normal') === undefined) {
    geometry1.computeVertexNormals();
  }
  if (geometry1.boundsTree === undefined) {
    geometry1.computeBoundsTree();
  }
  if (geometry2.getAttribute('normal') === undefined) {
    geometry2.computeVertexNormals();
  }
  if (geometry2.boundsTree === undefined) {
    geometry2.computeBoundsTree();
  }

  const brush1 = new Brush(geometry1);
  brush1.material = new MeshBasicMaterial({ color: 0xff0000, side: DoubleSide });
  const brush2 = new Brush(geometry2);
  brush2.material = new MeshBasicMaterial({ color: 0x0000ff, side: DoubleSide });
  brush1.geometry.setAttribute('uv', new Float32BufferAttribute([], 1));
  brush2.geometry.setAttribute('uv', new Float32BufferAttribute([], 1));

  const evaluator = new Evaluator();
  const result = evaluator.evaluate(brush1, brush2, operation);
  result.prepareGeometry();
  return result.geometry;
}

function getIndicesIntersection(mesh1: Mesh, mesh2: Mesh): Array<number> {
  const vertexIndices = new Set<number>(); // Use a Set to avoid duplicates

  // Matrix to transform mesh2's coordinates to mesh1's local space
  const matrix2to1 = new Matrix4().copy(mesh1.matrixWorld).invert().multiply(mesh2.matrixWorld);

  // Ensure bounds trees exist for both geometries
  if (!mesh1.geometry.boundsTree || !mesh2.geometry.boundsTree) {
    throw new Error('BoundsTree not found for three');
  }

  const edge = new Line3();

  // Perform BVH intersection using bounds trees
  mesh1.geometry.boundsTree.bvhcast(mesh2.geometry.boundsTree, matrix2to1, {
    intersectsTriangles(triangle1: ExtendedTriangle, triangle2: ExtendedTriangle, i1: number): boolean {
      // If the triangles intersect, capture the vertex indices from mesh1
      if (triangle1.intersectsTriangle(triangle2, edge)) {
        const i3 = i1 * 3;
        vertexIndices.add(mesh1.geometry.getIndex()?.getX(i3));
        vertexIndices.add(mesh1.geometry.getIndex()?.getY(i3));
        vertexIndices.add(mesh1.geometry.getIndex()?.getZ(i3));
      }

      return false; // Return false as we are not stopping at the first intersection
    },
  });

  // Convert Set to Array to return the list of unique vertex indices
  return Array.from(vertexIndices);
}

function findNearestUnvisitedPoint(point1: Vector3, points: Set<Vector3>, visited: Set<Vector3>, positions: Float32Array): Vector3 | null {
  let minDistance = Infinity;
  let nearestPoint: Vector3 | null = null;
  points.forEach((p) => {
    const point2 = new Vector3().fromBufferAttribute(positions, p);
    if (!visited.has({ x: point2.x, y: point2.y, z: point2.z })) {
      const distance = point1.distanceTo(point2);
      if (distance < minDistance && distance > 0.0) {
        minDistance = distance;
        nearestPoint = point2;
      }
    }
  });
  return nearestPoint;
}

function createShapeFromCurve(curve: Curve<Vector3>, segments = 50): BufferGeometry {
  const points = curve.getPoints(segments);
  const vertices = [];

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    vertices.push(p1.x, p1.y, p1.z);
    vertices.push(p2.x, p2.y, p2.z);
    vertices.push(0, 0, 0); // Center point for triangulation
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(vertices, 3));
  geometry.setIndex(new Uint32BufferAttribute(createIndicesForTriangles(points.length), 1));
  geometry.computeVertexNormals();

  return geometry;
}

function exportMeshToSTL(object: Object3D): string | DataView {
  const exporter = new STLExporter();
  const options = {
    binary: true,
  };
  return exporter.parse(object, options);
}

const MeshUtils = {
  createGeometry: createGeometry,
  createSubMesh: createSubMesh,
  solidModelling: solidModelling,
  getIndicesIntersection,
  findNearestUnvisitedPoint,
  createShapeFromCurve,
  exportMeshToSTL,
};
export default MeshUtils;
